import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail, RefreshCw, ArrowLeft, Heart, Inbox } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { signInAnonymously } from 'firebase/auth'
import { auth } from '../services/firebase'
import { getRandomLetter, Letter } from '../services/api'
import LetterCard from '../components/LetterCard'
import { logPageView, logLetterRequested, logLetterRead, logNoMoreLetters, logButtonClick } from '../services/analytics'

export default function ReadPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [letter, setLetter] = useState<Letter | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [noMoreLetters, setNoMoreLetters] = useState(false)
  const [hasRequested, setHasRequested] = useState(false)

  // Log page view
  useEffect(() => {
    logPageView('read')
  }, [])

  const fetchRandomLetter = async () => {
    setError(null)
    setNoMoreLetters(false)
    logLetterRequested()
    logButtonClick('get_letter', 'read')

    // Ensure user is authenticated
    let currentUser = user
    if (!currentUser) {
      try {
        const result = await signInAnonymously(auth)
        currentUser = result.user
      } catch {
        setError('Giriş yapılamadı. Lütfen tekrar deneyin.')
        return
      }
    }

    setIsLoading(true)
    setHasRequested(true)

    try {
      const result = await getRandomLetter()
      
      if (result.success && result.letter) {
        setLetter(result.letter)
        logLetterRead(result.letter.id)
      } else if (result.noMoreLetters) {
        setNoMoreLetters(true)
        setLetter(null)
        logNoMoreLetters()
      } else {
        setError(result.error || 'Bir hata oluştu.')
      }
    } catch {
      setError('Bir hata oluştu. Lütfen tekrar deneyin.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Back button */}
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-ink/60 hover:text-ink transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="font-body text-sm">Ana Sayfa</span>
      </button>

      {/* Header */}
      <div className="text-center mb-8 animate-fade-in">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-rose-100 to-rose-200 rounded-full mb-4">
          <Mail className="w-8 h-8 text-rose-600" />
        </div>
        <h1 className="font-display text-3xl font-semibold text-ink mb-2">
          Mektup Oku
        </h1>
        <p className="font-body text-ink/70">
          Havuzdan rastgele bir mektup al ve oku
        </p>
      </div>

      {/* Content Area */}
      <div className="animate-fade-in">
        {/* Initial State - No letter requested yet */}
        {!hasRequested && !isLoading && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-rose-100 p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-6 relative">
              <div className="envelope w-full h-full rounded-lg shadow-lg">
                <div className="heart-seal" />
              </div>
            </div>
            <h2 className="font-display text-xl font-semibold text-ink mb-4">
              Bir Mektup Almak İster misin?
            </h2>
            <p className="font-body text-ink/70 mb-8 max-w-md mx-auto">
              Havuzdan sana özel bir mektup seçilecek. Mektup rastgele atanır ve 
              kendi yazdığın mektuplar sana gelmez.
            </p>
            <button
              onClick={fetchRandomLetter}
              className="btn-primary flex items-center gap-2 mx-auto"
            >
              <Mail className="w-5 h-5" />
              Mektup Al
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-rose-100 p-12 text-center">
            <div className="spinner mx-auto mb-6" />
            <p className="font-body text-ink/70">Mektubun aranıyor...</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-red-100 p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">😢</span>
            </div>
            <h2 className="font-display text-xl font-semibold text-ink mb-2">
              Bir Sorun Oluştu
            </h2>
            <p className="font-body text-red-600 mb-6">{error}</p>
            <button
              onClick={fetchRandomLetter}
              className="btn-secondary flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Tekrar Dene
            </button>
          </div>
        )}

        {/* No More Letters State */}
        {noMoreLetters && !isLoading && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-rose-100 p-12 text-center">
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Inbox className="w-8 h-8 text-rose-400" />
            </div>
            <h2 className="font-display text-xl font-semibold text-ink mb-4">
              Şu An Mektup Yok
            </h2>
            <p className="font-body text-ink/70 mb-8 max-w-md mx-auto">
              Havuzda okunabilecek mektup kalmadı veya tüm mektupları zaten okudun. 
              Daha sonra tekrar dene!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate('/write')}
                className="btn-primary"
              >
                Mektup Yaz
              </button>
              <button
                onClick={fetchRandomLetter}
                className="btn-secondary flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Tekrar Dene
              </button>
            </div>
          </div>
        )}

        {/* Letter Display */}
        {letter && !isLoading && !error && (
          <div className="space-y-6">
            <LetterCard body={letter.body} />
            
            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <button
                onClick={fetchRandomLetter}
                className="btn-primary flex items-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Başka Mektup Al
              </button>
              <button
                onClick={() => navigate('/write')}
                className="btn-secondary flex items-center gap-2"
              >
                <Heart className="w-5 h-5" />
                Sen de Yaz
              </button>
            </div>

            {/* Encouragement */}
            <p className="text-center text-ink/50 font-body text-sm">
              💕 Bu mektup birileri için yazıldı, belki de senin için...
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
